import { useQuery } from '@tanstack/react-query';
import { TZDate } from '@date-fns/tz';
import { useApi, useAuth } from '../../hooks';
import { CareModel } from '../../models/user';
import { formatDateToDayOnly } from '../../components/AppointmentsPage/utils';
import {
  MeetingInfo,
  MeetingType,
  ProviderAvailabilityEntry,
} from '../../models/meeting';
import { ProviderType } from '../types';
import { AppointmentProviderType } from '../../models/appointment-type';
import qs from 'qs';

export type ProviderInfo = {
  availability: ProviderAvailabilityEntry[];
  display_name: string;
  image_url: string;
  previous_meeting: string | null;
  provider_id: number;
  provider_type: string;
};

export type ProviderAvailabilityResponse = {
  dates: string[];
  meeting_type: MeetingInfo;
  providers: ProviderInfo[];
};

type Props = {
  care_model?: CareModel;
  careMemberType?: AppointmentProviderType;
  endDate?: TZDate;
  meetingType?: MeetingType | null;
  providerType?: ProviderType | null;
  startDate?: TZDate;
  timezone?: string;
  userId?: string;
};

export const useProviderAvailability = ({
  care_model,
  careMemberType: care_member_type,
  endDate,
  meetingType: meeting_type,
  providerType,
  startDate,
  timezone,
  userId: user_id,
}: Props) => {
  const { api } = useApi();
  const { userToken } = useAuth();

  const fetchAvailability = async (): Promise<ProviderAvailabilityResponse> => {
    if (!startDate || !endDate) {
      return { availability: [] } as unknown as ProviderAvailabilityResponse;
    }

    const start_date = formatDateToDayOnly(startDate);
    const end_date = formatDateToDayOnly(endDate);
    const care_member_category = providerType?.toLowerCase() ?? null;

    const queryStr = qs.stringify({
      appt_type: meeting_type,
      care_member_category,
      care_member_type,
      end_date,
      start_date,
      timezone,
      user_id,
    });

    return api({
      route: `/private/provider/availability/${
        queryStr ? `?${queryStr}` : ''
      }`,
      token: userToken,
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'Coach-Override': user_id ?? '',
      },
    });
  };

  const { data, isInitialLoading, isLoading, isError } = useQuery<
    ProviderAvailabilityResponse,
    Error
  >({
    queryKey: [
      'provider-availability',
      user_id,
      endDate,
      meeting_type,
      startDate,
      timezone,
      providerType,
      care_member_type,
    ],
    queryFn: fetchAvailability,
    enabled:
      !!care_model &&
      !!user_id &&
      !!endDate &&
      !!meeting_type &&
      !!providerType &&
      !!startDate &&
      !!timezone,
    refetchOnWindowFocus: false,
  });

  return {
    isLoading: isInitialLoading || isLoading,
    isError,
    data,
  };
};
